import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider.jsx';
import { cartApi } from '../lib/cart/cartApi.js';
import { brandFromReturnUrl } from '../lib/brand/brandFromReturnUrl.js';

const STAGES = [
  { id: 'connecting', label: 'Connecting to secure gateway', desc: 'Loading Razorpay…' },
  { id: 'ready', label: 'Awaiting your payment', desc: 'Complete payment in the Razorpay window.' },
  { id: 'verifying', label: 'Verifying your payment', desc: 'Confirming with your bank — this usually takes a few seconds.' },
  { id: 'done', label: 'Payment confirmed', desc: 'Redirecting you back…' },
];

export default function CheckoutPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = Number(params.get('session') || '0');
  const { accessToken, user } = useAuth();
  const [stage, setStage] = useState('connecting');
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const [waitedSec, setWaitedSec] = useState(0);
  const rzRef = useRef(null);

  const brand = brandFromReturnUrl(session?.return_to_url);

  useEffect(() => {
    if (!sessionId) { setError('Missing checkout session.'); return; }
    if (!accessToken) {
      setError('Please sign in on the brand site, then come back to checkout.');
      return;
    }
    if (!window.Razorpay) {
      setError('Payment SDK failed to load. Refresh and try again.');
      return;
    }

    let cancelled = false;

    cartApi.pollCheckoutSession(sessionId, accessToken)
      .then((s) => {
        if (cancelled) return;
        setSession(s);
        if (s.status === 'paid') {
          nav(`/order-confirmation?session=${sessionId}`);
          return;
        }
        setStage('ready');
        const rz = new window.Razorpay({
          key: import.meta.env.VITE_RAZORPAY_KEY_ID || '',
          order_id: s.razorpay_order_id,
          name: 'SuperHomes',
          description: `Purchase via ${brandFromReturnUrl(s.return_to_url)?.name || 'SuperHomes brand'}`,
          prefill: {
            name: user?.name || '',
            email: user?.email || '',
            contact: user?.phone || '',
          },
          theme: { color: '#F47A1F' },
          handler: () => {
            setStage('verifying');
            pollUntilPaid(sessionId, accessToken, setWaitedSec).then(() => {
              setStage('done');
              setTimeout(() => nav(`/order-confirmation?session=${sessionId}`), 600);
            }).catch(() => {
              setError(
                'Payment received but confirmation is taking longer than usual. ' +
                'Your order is safe — we\'ll email you the receipt within a few minutes.'
              );
            });
          },
          modal: {
            ondismiss: () => setStage('cancelled'),
          },
        });
        rzRef.current = rz;
        rz.open();
      })
      .catch((e) => setError(e?.detail?.errors?.[0]?.message || 'Could not load checkout session.'));

    return () => { cancelled = true; };
  }, [sessionId, accessToken, user, nav]);

  const reopenRazorpay = () => {
    if (rzRef.current) {
      setStage('ready');
      rzRef.current.open();
    }
  };

  const returnToBrand = () => {
    if (brand?.origin) window.location.href = brand.origin;
    else nav('/');
  };

  if (error) {
    return (
      <Shell brand={brand}>
        <Card>
          <Icon kind="warn" />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 mt-4">Hold on…</h1>
          <p className="text-slate-600 mt-2 leading-relaxed">{error}</p>
          <div className="mt-7 flex flex-col gap-2">
            <button onClick={returnToBrand} className="w-full bg-slate-900 text-white font-semibold rounded-xl py-3 hover:bg-slate-800 transition">
              {brand ? `Return to ${brand.name}` : 'Return to SuperHomes'}
            </button>
            <button onClick={() => window.location.reload()} className="w-full text-slate-600 font-medium rounded-xl py-3 hover:bg-slate-100 transition">
              Try again
            </button>
          </div>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell brand={brand}>
      <Card>
        <Stages active={stage} />
        {stage === 'cancelled' && (
          <div className="mt-5 text-center">
            <p className="text-slate-700 font-medium">You closed the payment window.</p>
            <p className="text-slate-500 text-sm mt-1">Your cart is saved — try again whenever you're ready.</p>
            <div className="mt-5 flex flex-col gap-2">
              <button onClick={reopenRazorpay} className="w-full bg-superhomes-500 hover:bg-superhomes-600 text-white font-semibold rounded-xl py-3 transition">
                Reopen payment
              </button>
              <button onClick={returnToBrand} className="w-full text-slate-600 font-medium rounded-xl py-3 hover:bg-slate-100 transition">
                {brand ? `Back to ${brand.name}` : 'Back to SuperHomes'}
              </button>
            </div>
          </div>
        )}
        {stage === 'verifying' && waitedSec > 8 && (
          <p className="mt-4 text-xs text-slate-400 text-center">
            Still working… banks occasionally take a bit. We won't time out.
          </p>
        )}
      </Card>
      <p className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-1.5">
        <LockIcon /> Secured by Razorpay · 256-bit SSL · PCI DSS Level 1
      </p>
    </Shell>
  );
}

function Shell({ brand, children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/40 flex flex-col">
      <header className="px-6 py-5 flex items-center justify-between border-b border-slate-200/60 bg-white/60 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-superhomes-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm">S</div>
          <span className="font-bold text-slate-900 tracking-tight">SuperHomes Checkout</span>
        </div>
        {brand && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Paying</div>
            <div className="text-sm font-semibold text-slate-700">{brand.name}</div>
          </div>
        )}
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="bg-white rounded-2xl shadow-xl ring-1 ring-slate-900/5 p-8">
      {children}
    </div>
  );
}

function Stages({ active }) {
  const activeIdx = STAGES.findIndex((s) => s.id === active);
  const isDone = active === 'done';
  return (
    <div>
      <div className="flex items-center justify-center mb-6">
        {isDone ? <Icon kind="success" /> : <Spinner />}
      </div>
      <ol className="space-y-3">
        {STAGES.map((s, i) => {
          const status = i < activeIdx || isDone ? 'done' : i === activeIdx ? 'active' : 'pending';
          return (
            <li key={s.id} className="flex items-start gap-3">
              <StageDot status={status} />
              <div className="flex-1">
                <div className={`text-sm font-semibold ${status === 'pending' ? 'text-slate-400' : 'text-slate-900'}`}>
                  {s.label}
                </div>
                {status === 'active' && (
                  <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function StageDot({ status }) {
  if (status === 'done') {
    return (
      <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg width="10" height="10" viewBox="0 0 20 20" fill="none"><path d="M5 10l3 3 7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
    );
  }
  if (status === 'active') {
    return <div className="w-5 h-5 rounded-full border-2 border-superhomes-500 bg-superhomes-500/10 flex-shrink-0 mt-0.5 animate-pulse" />;
  }
  return <div className="w-5 h-5 rounded-full border-2 border-slate-200 flex-shrink-0 mt-0.5" />;
}

function Spinner() {
  return (
    <div className="w-14 h-14 rounded-full border-4 border-slate-100 border-t-superhomes-500 animate-spin" />
  );
}

function Icon({ kind }) {
  if (kind === 'success') {
    return (
      <div className="w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
    );
  }
  if (kind === 'warn') {
    return (
      <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 9v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
    );
  }
  return null;
}

function LockIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M4 7V5a4 4 0 018 0v2m-9 0h10v7H3V7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
  );
}

async function pollUntilPaid(sessionId, token, onTick, attempts = 60) {
  for (let i = 0; i < attempts; i++) {
    const s = await cartApi.pollCheckoutSession(sessionId, token);
    if (s.status === 'paid') return s;
    if (s.status === 'failed' || s.status === 'abandoned') throw new Error('Payment did not complete');
    onTick && onTick((i + 1) * 2);
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new Error('Timed out waiting for confirmation');
}
